<script setup lang="ts">
import { computed } from 'vue'

import { cartFeeLines } from '@tix/cart'
import BaseCard from '@tix/ds/components/atoms/BaseCard.vue'
import type { Cart } from '@tix/entity'

import { findDonationTicket } from '@/helpers/donationHelpers'
import { getCartTrackableData } from '@/helpers/errorContext'

import { errorTracker } from '@/services/errorTracking'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PriceDisplay from '@/components/elements/PriceDisplay.vue'

type Props = {
  cart?: Cart | null
  pending?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  cart: null,
  pending: false,
})

const feeSummary = computed(() => {
  const totals = props.cart?.stripeFee?.grand_totals
  if (!totals) {
    return null
  }

  return {
    fixedFees: parseFloat(totals.total_fee_fixed_outside ?? '0'),
    percentFees: parseFloat(totals.total_fee_percent_outside ?? '0'),
    total: parseFloat(totals.total_outstanding),
    discount: parseFloat(totals.total_cartmod_amount ?? '0'),
  }
})

const cartFees = computed(() => {
  const current = props.cart
  if (!current) {
    return []
  }

  const { data, error } = cartFeeLines(
    current.tickets ?? [],
    current.ticketGroups ?? [],
    current.ticketTypes ?? [],
    current.eventTemplates ?? [],
  )
  if (error) {
    errorTracker.track('Unable to build cart fee lines', {
      cause: error,
      cart: getCartTrackableData(current),
    })
    return []
  }

  return data ?? []
})

const fixedFees = computed(() => feeSummary.value?.fixedFees ?? 0)
const percentFees = computed(() => feeSummary.value?.percentFees ?? 0)
const total = computed(() => feeSummary.value?.total ?? 0)
const discount = computed(() => feeSummary.value?.discount ?? 0)
const subtotal = computed(() => {
  const faceValue = findDonationTicket(props.cart)?.face_value
  return faceValue != null ? Number(faceValue) : 0
})
const hasFees = computed(() => fixedFees.value > 0 || percentFees.value > 0 || cartFees.value.length > 0)
const hasDiscounts = computed(() => discount.value !== 0)
const visible = computed(() => props.pending || total.value > 0 || hasDiscounts.value)
</script>

<template>
  <div v-if="visible" class="donation-cart-total-container">
    <h2 class="donation-cart-total-heading">Payment information</h2>
    <BaseCard v-if="visible" aria-live="polite" padding="comfy">
      <div class="content">
        <div v-if="hasFees || hasDiscounts" class="row subtotal">
          <!-- TODO TIC-6558 Use language strings. -->
          <h3 class="section-heading">Subtotal</h3>
          <LoadingSpinner v-if="pending" />
          <span v-else class="amount">
            <PriceDisplay :value="subtotal" />
          </span>
        </div>

        <div v-if="hasFees" class="section">
          <h3 class="section-heading">Fees</h3>

          <div v-if="fixedFees > 0" class="row indented">
            <!-- TODO TIC-6558 Use language strings. -->
            <span>Service charges</span>
            <LoadingSpinner v-if="pending" />
            <span v-else class="amount">
              <PriceDisplay :value="fixedFees" />
            </span>
          </div>

          <div v-if="percentFees > 0" class="row indented">
            <!-- TODO TIC-6558 Use language strings. -->
            <span>Tax</span>
            <LoadingSpinner v-if="pending" />
            <span v-else class="amount">
              <PriceDisplay :value="percentFees" />
            </span>
          </div>

          <div v-for="line in cartFees" :key="line.ticketTypeId" class="row indented">
            <span>{{ line.label }}</span>
            <LoadingSpinner v-if="pending" />
            <span v-else class="amount">
              <PriceDisplay :value="line.amount" />
            </span>
          </div>
        </div>

        <div v-if="hasDiscounts" class="row">
          <!-- TODO TIC-6558 Use language strings. -->
          <h3 class="section-heading">Discounts</h3>

          <LoadingSpinner v-if="pending" />
          <span v-else class="amount">
            <PriceDisplay :value="discount" />
          </span>
        </div>

        <div class="row total">
          <!-- TODO TIC-6558 Use language strings. -->
          <h3>Total</h3>
          <LoadingSpinner v-if="pending" />
          <span v-else class="donation-cart-total-value">
            <PriceDisplay :value="total" />
          </span>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<style scoped>
.donation-cart-total-container {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5, 24px);
}

.donation-cart-total-heading {
  font-size: var(--font-size-h3);
}

.amount {
  font-weight: var(--font-weight-normal);
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  .section {
    display: flex;
    flex-direction: column;
  }

  .section-heading {
    font-family: var(--font-family-heading);
    font-size: var(--font-size-body);
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height-body);
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.indented {
      padding-left: var(--space-1);
    }

    &:not(:first-child).total {
      border-top: var(--divider);
      padding-top: var(--space-1);
    }

    &.total {
      font-size: var(--font-size-h3);
      font-weight: var(--font-weight-bold);
    }

    &.subtotal {
      font-weight: var(--font-weight-bold);
    }
  }

  .amount {
    font-weight: var(--font-weight-normal);
  }
}
</style>
